// @flow

import * as React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Text, View } from 'reshaped';

import { withPageMeta } from '@gandi/app-runner';
import { addCartProduct, getCart } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useHandleFormErrorSubmit } from 'src/Cart/ContactForm/hooks/useHandleFormError';
import PreferencesWidget from 'src/Layout/components/PreferencesWidget/PreferencesWidget';
import { ScrollIntoView } from 'src/Reshaped/ScrollIntoView';
import { Layout } from 'src/components/Layout';
import type { FullState, PreFetchArgs } from 'src/types';

import { useRequireAuthNavigate } from '../hooks/useRequireAuthNavigate';

import {
  DomainsList,
  formatDomainsPayload,
} from './components/Acme/DomainsList';
import { FeaturesBanner } from './components/Acme/FeaturesBanner';
import { Offers } from './components/Acme/Offers';
import { SelectField } from './components/Acme/SelectField';
import { SubmitButton } from './components/Acme/SubmitButton';
import { SubscriptionNameField } from './components/Acme/SubscriptionNameField';

import style from './ACMECertificate.module.css';

type Data = {
  provider: 'sectigo',
  validation_type: 'dv' | 'ov',
  name: string,
  duration: string,
  domains: string,
};

function ACMECertificate(): React.Node {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const [domainsConfirmed, setDomainsConfirmed] = React.useState(false);

  const [cartId, setCartId] = React.useState<string>('');
  const navigateToCart = useRequireAuthNavigate(`/cart/${cartId}`);
  const form = useForm({
    defaultValues: {
      provider: 'sectigo',
      validation_type: 'dv',
      name: __('My ACME subscription'),
      duration: '1',
      domains: '',
    },
    shouldUnregister: true,
    mode: 'onChange',
  });

  const formErrorCallback = useHandleFormErrorSubmit(form);

  React.useEffect(() => {
    if (!cartId) return;

    navigateToCart();
  }, [cartId, navigateToCart]);

  const formData = form.getValues();
  const formFieldNames = Object.keys(formData);
  const onSubmit = React.useCallback(
    (data: Data) => {
      dispatch(
        addCartProduct({
          productData: {
            product_type: 'acme',
            process: 'create',
            payload: {
              provider: data.provider,
              name: data.name?.trim(),
              validation_type: data.validation_type,
              duration: Number.parseInt(data.duration, 10),
              domains: formatDomainsPayload(data.domains),
            },
          },
          options: {
            headers: { 'X-Dry-Run': 0 },
          },
        }),
      )
        .then(resp => {
          const respData = resp.data;
          const errors = respData.errors;

          // Only throw if there are errors matching form fields
          // Ignore errors on fields that aren't in the form (e.g.: prices, currency, ...)
          const hasFormRelatedErrors = errors?.some(err =>
            formFieldNames.includes(err.name),
          );

          if (hasFormRelatedErrors) {
            throw resp;
          }

          dispatch(getCart()).then(response => {
            // $FlowFixMe[prop-missing]
            const respCartId = response.data?.uuid as any as ?string;
            setCartId(respCartId || '');
          });
        })
        .catch(err => {
          setCartId('');
          formErrorCallback({ response: err });
        });
    },
    [dispatch, formErrorCallback, formFieldNames],
  );

  return (
    <Layout>
      <View
        attributes={{
          style: {
            marginBottom: 'calc(var(--rs-unit-x1) * 6)',
          },
        }}
      >
        <Text
          as="h1"
          variant="featured-3"
          weight="bold"
          attributes={{
            style: { marginTop: '0', marginBottom: 'var(--rs-unit-x1)' },
          }}
        >
          {__('Create your ACME subscription')}
        </Text>

        <Text variant="body-2">
          {__(
            'Automate the management of your TLS certificates. Choose the best option to issue, renew, and revoke certificates securely, without any manual intervention.',
          )}
        </Text>
      </View>

      <FeaturesBanner />

      <FormProvider {...form}>
        {/* $FlowFixMe[prop-missing] */}
        <View
          as="form"
          gap={12}
          attributes={{
            id: 'certificate-form',
            onSubmit: form.handleSubmit(onSubmit),
          }}
          className={style.root}
        >
          <Offers />

          <SubscriptionNameField />

          <DomainsList
            setDomainsConfirmed={setDomainsConfirmed}
            domainsConfirmed={domainsConfirmed}
          />

          {domainsConfirmed && (
            <ScrollIntoView>
              <SelectField />
              <SubmitButton />
            </ScrollIntoView>
          )}
        </View>
      </FormProvider>
    </Layout>
  );
}

ACMECertificate.preFetch = (args: PreFetchArgs<{}, void, empty, FullState>) =>
  PreferencesWidget.preFetch(args);

const ACMECertificateWithMeta = (withPageMeta(args => ({
  title: args.__('Buy ACME Certificate - Gandi.net'),
  description: args.__(
    'ACME Certificate - Secure your transactions with Gandi\'s large offer of certificates (free certificates, Organization validation, Extendend validation "Green bar")',
  ),
}))(ACMECertificate): React.ComponentType<{}>);

export { ACMECertificateWithMeta as ACMECertificate };
